// Pure helpers for the Unsigned schedule graphics: Danish-time week windows, the X caption, and
// X's weighted character count. Everything here is in Europe/Copenhagen regardless of where the
// admin's browser is - these posts are for Danish viewers.

import { UnsignedMatch } from "../../services/UnsignedGraphicsService";

const TZ = "Europe/Copenhagen";

export interface DanishTime {
    year: number;
    month: number; // 1-12
    day: number;
    hour: number;
    minute: number;
    weekday: number; // 0 = Monday ... 6 = Sunday
    offsetHours: number; // 1 = CET, 2 = CEST
}

const partsFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    weekday: "short",
    hourCycle: "h23",
});

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const toDanish = (date: Date): DanishTime => {
    const p: Record<string, string> = {};
    for (const part of partsFormatter.formatToParts(date)) p[part.type] = part.value;
    const t = {
        year: +p.year,
        month: +p.month,
        day: +p.day,
        hour: +p.hour,
        minute: +p.minute,
        weekday: WEEKDAYS.indexOf(p.weekday),
    };
    // Offset = Danish wall clock read as UTC, minus the real instant.
    const wallAsUtc = Date.UTC(t.year, t.month - 1, t.day, t.hour, t.minute);
    const offsetHours = Math.round((wallAsUtc - Math.floor(date.getTime() / 60000) * 60000) / 3600000);
    return { ...t, offsetHours };
};

export const zoneLabel = (t: DanishTime): string => (t.offsetHours === 2 ? "CEST" : "CET");

/** Danish calendar date of the Monday starting the week that `date` falls in, as a day number. */
const mondayDayNumber = (date: Date): number => {
    const t = toDanish(date);
    return Math.floor(Date.UTC(t.year, t.month - 1, t.day) / 86400000) - t.weekday;
};

export type WeekChoice = "this" | "next";

/**
 * Matches in the chosen Danish week. "this" is the rest of the current week (already-started
 * matches drop out), "next" is the whole of next Monday-Sunday.
 */
export const matchesInWeek = (matches: UnsignedMatch[], week: WeekChoice, now = new Date()): UnsignedMatch[] => {
    const target = mondayDayNumber(now) + (week === "next" ? 7 : 0);
    return matches
        .filter(m => {
            const at = new Date(m.matchTimeUtc);
            if (week === "this" && at.getTime() < now.getTime()) return false;
            return mondayDayNumber(at) === target;
        })
        .sort((a, b) => new Date(a.matchTimeUtc).getTime() - new Date(b.matchTimeUtc).getTime());
};

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Card time, as on the Canva cards: "29. kl 20:00". */
export const cardTime = (iso: string): string => {
    const t = toDanish(new Date(iso));
    return `${pad2(t.day)}. kl ${pad2(t.hour)}:${pad2(t.minute)}`;
};

/** Caption time, as in the posts: "3/3 kl 20.00 CET". */
export const captionTime = (iso: string): string => {
    const t = toDanish(new Date(iso));
    return `${t.day}/${t.month} kl ${pad2(t.hour)}.${pad2(t.minute)} ${zoneLabel(t)}`;
};

export const TBA = "TBA";

export const buildCaption = (
    teamName: string,
    matches: UnsignedMatch[],
    mentions: Record<string, string>,
    week: WeekChoice,
): string => {
    const header = `🚨${teamName} ${week === "next" ? "i næste uge" : "resten af denne uge"}!`;
    const blocks = matches.map(m => [
        "🇩🇰 @Dust2dk",
        "",
        `🕕${captionTime(m.matchTimeUtc)}`,
        `🆚 ${mentions[m.opponent.teamId]?.trim() || m.opponent.teamName}`,
        `🎙️${m.stream?.trim() || TBA}`,
    ].join("\n"));
    return [header, ...blocks].join("\n\n");
};

// ---- X character count -----------------------------------------------------------------------

export const X_LIMIT = 280;

// X counts a URL as 23 no matter its length, and detects bare domains like twitch.tv/name too.
const URL_RE = /(?:https?:\/\/)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s]*)?/gi;
// Built from a string: the project's ES5 TS target rejects the /u flag on a literal.
const EMOJI_RE = new RegExp("\\p{Extended_Pictographic}|\\p{Regional_Indicator}", "u");

// Code points X weighs as 1 (twitter-text v3 config); everything else - and every emoji - is 2.
const isLight = (cp: number) =>
    cp <= 4351 || (cp >= 8192 && cp <= 8205) || (cp >= 8208 && cp <= 8223) || (cp >= 8242 && cp <= 8247);

/** X's weighted length of a post: what its 280 limit is measured in. */
export const xLength = (text: string): number => {
    let urls = 0;
    const rest = text.normalize("NFC").replace(URL_RE, () => {
        urls++;
        return "";
    });
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    let n = urls * 23;
    for (const { segment } of Array.from(segmenter.segment(rest))) {
        if (EMOJI_RE.test(segment)) {
            n += 2;
            continue;
        }
        for (const ch of Array.from(segment)) n += isLight(ch.codePointAt(0)!) ? 1 : 2;
    }
    return n;
};
